/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Modal } from '@domain-group/fe-blueprint-ui/modal';
import { Button, LinkButton } from '@domain-group/fe-blueprint-ui/button';
import {
  ChevronRightIconSmall,
  ChevronLeftIconSmall,
  ExternalLinkIcon,
} from '@domain-group/fe-co-icon';
import Pagination from '../pagination';
import uniqueByKey from '../../../utils/array-unique-by-key';
import RmaLogo from './rma-logo';
import type { ReviewListEntry } from '../../../@types';
import useModalCarousel from './use-modal-carousel';
import Review from './review';
import VendorReview from './vendor-review';
import VendorFooter from './vendor-footer';
import * as reviewListStyles from './reviews-list.style';

const renderableReviews = (review: ReviewListEntry) =>
  !!review.title && !!review.description;

export type ReviewsListProps = {
  profileUrl: string;
  reviews: ReviewListEntry[];
  onPageRendered: () => void;
  onVendorRecoViewAll: () => void;
  onVendorRecoRendered: () => void;
  onVendorRecoImpression: () => void;
  onVendorRecoModalViewMore: () => void;
  onVendorRecoModalPrev?: () => void;
  onVendorRecoModalNext?: () => void;
  onVendorRecoShowMore?: () => void;
  starRatingsEnabled?: boolean;
};

const ReviewsList = ({
  reviews,
  profileUrl,
  onPageRendered,
  onVendorRecoViewAll,
  onVendorRecoRendered,
  onVendorRecoImpression,
  onVendorRecoModalViewMore,
  onVendorRecoModalPrev,
  onVendorRecoModalNext,
  onVendorRecoShowMore,
  starRatingsEnabled = false,
}: ReviewsListProps): JSX.Element => {
  const [modalReview, setModalReview] = useState<number | null>(null);
  const [nextItem, prevItem] = useModalCarousel(
    modalReview,
    setModalReview,
    reviews.length,
  );
  const [hasMoreReviews, setHasMoreReviews] = useState(false);
  const [activeReview, setActiveReview] = useState(1);

  const initialVisibleReviews = 5;
  const maxVisibleReviews = 50;

  /*
  Tracking related functions
  */
  const [pageHasRendered, setPageHasRendered] = useState(false);
  const [userWasImpressed, setUserWasImpressed] = useState(false);
  const [ref, inView] = useInView();

  useEffect(() => {
    if (!pageHasRendered) {
      onVendorRecoRendered();
      setPageHasRendered(true);
    }

    if (inView && !userWasImpressed) {
      onVendorRecoImpression();
      setUserWasImpressed(true);
    }
  }, [
    inView,
    onVendorRecoImpression,
    onVendorRecoRendered,
    userWasImpressed,
    pageHasRendered,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onPageRendered, []);

  const uniqueReviews = uniqueByKey<ReviewListEntry>(
    reviews.filter(renderableReviews),
    'description',
  );

  const displayedReviews = uniqueReviews.slice(
    0,
    hasMoreReviews ? initialVisibleReviews : maxVisibleReviews,
  );
  useEffect(() => {
    if (uniqueReviews.length > initialVisibleReviews) {
      setHasMoreReviews(true);
    } else {
      setHasMoreReviews(false);
    }
  }, [uniqueReviews.length]);

  /* istanbul ignore next */
  const updateDots = (visible: boolean, index: number) => {
    /* istanbul ignore next */
    if (visible) {
      /* istanbul ignore next */
      setActiveReview(index - 1);
    }
  };

  const onShowMoreClick = () => {
    setHasMoreReviews(false);
    if (onVendorRecoShowMore) {
      onVendorRecoShowMore();
    }
  };

  if (starRatingsEnabled) {
    return (
      <div>
        <ul css={[reviewListStyles.list, reviewListStyles.listVertical]}>
          {displayedReviews.map((review) => (
            <li
              css={[
                reviewListStyles.listItem,
                reviewListStyles.listItemVertical,
              ]}
              key={`${review.reviewerName}-${review.title}`}
            >
              <VendorReview
                {...review}
                listView
                descLength={265}
                onActivate={updateDots}
                recommendation="seller"
                onVendorRecoModalViewMore={onVendorRecoModalViewMore}
              />
            </li>
          ))}
        </ul>
        <VendorFooter
          onVendorRecoViewAll={onVendorRecoViewAll}
          profileUrl={profileUrl}
          hasMoreReviews={hasMoreReviews}
          onShowMoreClick={onShowMoreClick}
        />
      </div>
    );
  }

  return (
    <div>
      <header ref={ref} css={reviewListStyles.header}>
        <div css={reviewListStyles.headerContainer}>
          <h2 css={reviewListStyles.heading}>Recommendations</h2>
          <LinkButton
            href={profileUrl}
            appearance="link"
            icon={<ExternalLinkIcon />}
            iconPosition="right"
            target="_blank"
            rel="noopener"
            onClick={onVendorRecoViewAll}
            onAuxClick={onVendorRecoViewAll}
          >
            View all
          </LinkButton>
        </div>
        <span css={reviewListStyles.promo}>
          Provided by <RmaLogo height={20} marginTop={4} />
        </span>
        <p css={reviewListStyles.disclaimer}>
          Domain does not make any representation about the accuracy of the
          reviews which are provided by RateMyAgent.
        </p>
      </header>
      <ul css={reviewListStyles.list}>
        {uniqueReviews.map((review, index) => (
          <li
            css={reviewListStyles.listItem}
            key={`${review.reviewerName}-${review.title}`}
          >
            <Review
              {...review}
              listView
              descLength={265}
              openModal={() => {
                setModalReview(index);
                onVendorRecoModalViewMore();
              }}
              onActivate={updateDots}
            />
          </li>
        ))}
      </ul>
      <div css={reviewListStyles.paginationContainer}>
        <Pagination dots={uniqueReviews.length} index={activeReview} />
      </div>
      {modalReview !== null && (
        <Modal
          closeCallback={() => setModalReview(null)}
          hasCloseAnimation
          idealWidth={600}
        >
          <div css={reviewListStyles.modalReview}>
            <Review
              {...reviews[modalReview]}
              listView={false}
              showPropertyCard
            />
          </div>
          <div css={reviewListStyles.modalFooter}>
            <Button
              onClick={() => {
                prevItem();
                if (onVendorRecoModalPrev) {
                  onVendorRecoModalPrev();
                }
              }}
              appearance="link"
              icon={<ChevronLeftIconSmall />}
            >
              Previous recommendation
            </Button>
            <Button
              onClick={() => {
                nextItem();
                if (onVendorRecoModalNext) {
                  onVendorRecoModalNext();
                }
              }}
              appearance="link"
              icon={<ChevronRightIconSmall />}
              iconPosition="right"
            >
              Next recommendation
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ReviewsList;
